import { CSSProperties } from 'react'
import { useLocation } from 'react-router-dom'

import { FontAwesomeIconName, LegacyIconName } from 'components/Icon'
import useAuth from 'hooks/use-auth'
import { trans } from 'i18n/translate'
import { hasSupportAppPermission } from 'support/helpers/support-rbac'
import { testSuitesGet } from 'utils/endpoints-access'

type SubNavType = {
  name: string
  route: string
  state?: Record<string, any>
}

export interface navItemConfig {
  isActive: boolean
  name: string
  disabled: boolean
  icon: FontAwesomeIconName | LegacyIconName
  text: string
  route?: string
  state?: Record<string, any>
  props?: Record<string, any>
  showNotification?: boolean
  enabled: boolean
  showSeparator?: boolean
  style?: CSSProperties
  subnav?: SubNavType[]
}

export interface useNavigationHook {
  navigationConfigItems: navItemConfig[]
}

export function useNavigation({
  pendingRequestsByAssetId,
  userHasTeams
}: {
  // TODO: When utils.ts is converted to typescript and the function responses are type, need to add that here.
  pendingRequestsByAssetId: Record<string, any>
  userHasTeams: boolean
}): useNavigationHook {
  const { pathname: currentPathname } = useLocation()
  const {
    hasEndpointAccess,
    hasFaultInjection,
    hasFailureFlags,
    hasReliabilityManagement,
    session
  } = useAuth()

  const disableForOnboarding =
    currentPathname.startsWith('/onboarding') &&
    currentPathname !== '/onboarding/profile'

  const isOnboardingOrHasNoTeams = disableForOnboarding || !userHasTeams

  const navigationConfigItems: navItemConfig[] = [
    {
      isActive: currentPathname.startsWith('/reports'),
      name: 'reports',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'chartLineUp',
      text: 'Reports',
      props: { 'data-test-nav-company-dashboard': true },
      enabled: hasReliabilityManagement,
      subnav: [
        {
          name: 'Company Summary',
          route: '/reports/company'
        },
        {
          name: 'Team Risk',
          route: '/reports/team-risk'
        },
        {
          name: 'Team Score',
          route: '/reports/team-score'
        }
      ]
    },
    {
      isActive: currentPathname.startsWith('/getting-started'),
      name: 'getting-started',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'graduationCap',
      text: 'Getting Started',
      route: '/getting-started',
      props: { 'data-test-nav-getting-started': true },
      enabled: hasReliabilityManagement
    },
    {
      isActive: currentPathname.startsWith('/services'),
      name: 'services',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'chartNetwork',
      text: 'Services',
      route: '/services',
      state: {
        clearState: true
      },
      props: { 'data-test-nav-services': true },
      enabled: hasReliabilityManagement
    },
    {
      isActive:
        currentPathname.startsWith('/attacks') ||
        currentPathname.startsWith('/alfi'),
      name: 'attacks',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'flask',
      text: 'Experiments',
      route: '/attacks/infrastructure',
      enabled: hasFaultInjection
    },
    {
      isActive: currentPathname.startsWith('/scenarios'),
      name: 'scenarios',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'clipboardList',
      text: 'Scenarios',
      route: '/scenarios/my-scenarios',
      state: {
        clearSearchState: true
      },
      enabled: hasFaultInjection
    },
    {
      isActive: currentPathname.startsWith('/test-suites'),
      name: 'test-suites',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'books',
      text: trans('test_suites'),
      route: '/test-suites',
      props: { 'data-test-nav-test-suites': true },
      enabled: hasReliabilityManagement && hasEndpointAccess(testSuitesGet)
    },
    {
      isActive: currentPathname.startsWith('/failure-flags'),
      name: 'failure-flags',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'code',
      text: trans('failure_flags.label.plural'),
      route: '/failure-flags/list',
      props: { 'data-test-nav-failure-flags': true },
      enabled: hasFailureFlags
    },
    {
      isActive: currentPathname.startsWith('/gamedays'),
      name: 'gamedays',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'joystick',
      text: 'GameDays',
      route: '/gamedays',
      enabled: hasFaultInjection
    },
    {
      isActive: currentPathname.startsWith('/health-checks'),
      name: 'status-checks',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'wavePulse',
      text: trans('status_checks.label.plural'),
      route: '/health-checks/list',
      state: {
        clearSearchState: true
      },
      props: { 'data-test-nav-status-checks': true },
      enabled: true
    },
    {
      isActive: currentPathname.startsWith('/schedules'),
      name: 'schedules',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'calendar',
      text: 'Schedules',
      route: '/schedules',
      enabled: hasFaultInjection
    },
    {
      isActive: currentPathname.startsWith('/clients'),
      name: 'clients',
      disabled: isOnboardingOrHasNoTeams,
      icon: 'clients',
      text: 'Agents',
      route: '/clients',
      showNotification: Object.keys(pendingRequestsByAssetId).length > 0,
      enabled: true
    },
    {
      isActive: currentPathname === '/help',
      name: 'help',
      disabled: false,
      icon: 'messageQuestion',
      text: 'Help',
      route: '/help',
      enabled: true,
      showSeparator: true
    },
    {
      isActive:
        currentPathname.startsWith('/support/') ||
        currentPathname.startsWith('/supportnew/'),
      name: 'support',
      disabled: false,
      icon: 'support',
      text: 'Support App',
      route: '/support',
      enabled: hasSupportAppPermission(session)
    }
  ]

  return {
    navigationConfigItems
  }
}
